<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import camelcaseKeys from 'camelcase-keys';
import { debounce } from '@chatwoot/utils';
import { useAlert, useTrack } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import ContactAPI from 'dashboard/api/contacts';
import MessageAPI from 'dashboard/api/inbox/message';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import {
  buildContactableInboxesList,
  createContactSearcher,
  createNewContact,
  fetchContactableInboxes,
  mergeInboxDetails,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ContactSelector from 'dashboard/components-next/NewConversation/components/ContactSelector.vue';
import InboxSelector from 'dashboard/components-next/NewConversation/components/InboxSelector.vue';

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
});

const RESTRICTED_CHANNELS = [
  INBOX_TYPES.FB,
  INBOX_TYPES.INSTAGRAM,
  INBOX_TYPES.TIKTOK,
  INBOX_TYPES.WHATSAPP,
];

const searchContacts = createContactSearcher();
const store = useStore();
const { t } = useI18n();
const { getPlainText } = useMessageFormatter();

const dialogRef = ref(null);
const contacts = ref([]);
const selectedContact = ref(null);
const targetInbox = ref(null);
const targetInboxes = ref([]);
const showContactsDropdown = ref(false);
const showInboxesDropdown = ref(false);
const isSearching = ref(false);
const isCreatingContact = ref(false);
const isFetchingInboxes = ref(false);
const isForwarding = ref(false);

const inboxesList = useMapGetter('inboxes/getInboxes');
const currentAccountId = useMapGetter('getCurrentAccountId');

const conversationId = computed(
  () => props.message.conversation_id ?? props.message.conversationId
);
const sourceInboxId = computed(
  () => props.message.inbox_id ?? props.message.inboxId
);
const previewContent = computed(() =>
  getPlainText(props.message.content || '')
);
const attachmentCount = computed(() => props.message.attachments?.length || 0);
const contactableInboxesList = computed(() =>
  buildContactableInboxesList(targetInboxes.value)
);
const isConfirmDisabled = computed(
  () => !selectedContact.value || !targetInbox.value
);
const shouldShowNoTargets = computed(
  () =>
    selectedContact.value &&
    !isFetchingInboxes.value &&
    targetInboxes.value.length === 0
);

const isRestrictedInbox = inbox => {
  if (inbox.channelType === INBOX_TYPES.TWILIO && inbox.medium === 'whatsapp') {
    return true;
  }

  if (inbox.channelType === INBOX_TYPES.API) {
    return !!inbox.additionalAttributes?.agentReplyTimeWindow;
  }

  return RESTRICTED_CHANNELS.includes(inbox.channelType);
};

const reset = () => {
  contacts.value = [];
  selectedContact.value = null;
  targetInbox.value = null;
  targetInboxes.value = [];
  showContactsDropdown.value = false;
  showInboxesDropdown.value = false;
  isSearching.value = false;
  isCreatingContact.value = false;
  isFetchingInboxes.value = false;
  isForwarding.value = false;
};

const onContactSearch = debounce(
  async query => {
    isSearching.value = true;
    contacts.value = [];
    try {
      const results = await searchContacts(query, { reachableOnly: false });
      if (results === null) return;
      contacts.value = results;
    } catch {
      useAlert(
        t('CONVERSATION.CONTEXT_MENU.FORWARD_MESSAGE.CONTACT_SEARCH_ERROR')
      );
    } finally {
      isSearching.value = false;
    }
  },
  400,
  false
);

const handleContactSearch = query => {
  showContactsDropdown.value = query.trim().length > 1;
  onContactSearch(query);
};

const handleDropdownUpdate = (_, value) => {
  showContactsDropdown.value = value;
};

const buildTargetInboxes = (contactableInboxes, conversations) => {
  const normalizedInboxes = inboxesList.value.map(inbox =>
    camelcaseKeys(inbox, { deep: true })
  );
  const accessibleInboxIds = new Set(
    normalizedInboxes.map(inbox => Number(inbox.id))
  );
  const mergedContactable = mergeInboxDetails(
    contactableInboxes,
    inboxesList.value
  ).filter(inbox => accessibleInboxIds.has(Number(inbox.id)));
  const contactableByInboxId = new Map(
    mergedContactable.map(inbox => [Number(inbox.id), inbox])
  );
  const sourceConversationInboxIds = new Set(
    conversations
      .filter(conversation => Number(conversation.id) === conversationId.value)
      .map(conversation => Number(conversation.inboxId))
  );
  const replyableByInboxId = new Map();

  conversations.forEach(conversation => {
    const inboxId = Number(conversation.inboxId);
    if (!conversation.canReply || sourceConversationInboxIds.has(inboxId)) {
      return;
    }

    const inbox = normalizedInboxes.find(item => Number(item.id) === inboxId);
    if (inbox && !replyableByInboxId.has(inboxId)) {
      replyableByInboxId.set(inboxId, {
        ...inbox,
        ...contactableByInboxId.get(inboxId),
        conversationId: conversation.id,
      });
    }
  });

  mergedContactable.forEach(inbox => {
    const inboxId = Number(inbox.id);
    if (sourceConversationInboxIds.has(inboxId)) return;
    if (isRestrictedInbox(inbox) && !replyableByInboxId.has(inboxId)) return;
    if (!replyableByInboxId.has(inboxId)) {
      replyableByInboxId.set(inboxId, inbox);
    }
  });

  return [...replyableByInboxId.values()];
};

const loadTargetInboxes = async contact => {
  isFetchingInboxes.value = true;
  targetInbox.value = null;
  targetInboxes.value = [];
  try {
    const [contactableInboxes, conversationsResponse] = await Promise.all([
      fetchContactableInboxes(contact.id),
      ContactAPI.getConversations(contact.id),
    ]);
    const conversations = camelcaseKeys(
      conversationsResponse.data.payload || [],
      { deep: true }
    );
    targetInboxes.value = buildTargetInboxes(contactableInboxes, conversations);

    targetInbox.value =
      targetInboxes.value.find(
        inbox => Number(inbox.id) === Number(sourceInboxId.value)
      ) || (targetInboxes.value.length === 1 ? targetInboxes.value[0] : null);
    showInboxesDropdown.value =
      targetInboxes.value.length > 1 && !targetInbox.value;
  } catch {
    useAlert(t('CONVERSATION.CONTEXT_MENU.FORWARD_MESSAGE.TARGETS_ERROR'));
  } finally {
    isFetchingInboxes.value = false;
  }
};

const handleSelectedContact = async ({ value, action, ...contactData }) => {
  let contact = contactData;
  if (action === 'create') {
    isCreatingContact.value = true;
    try {
      contact = await createNewContact(value);
    } catch (error) {
      const message = parseAPIErrorResponse(error);
      useAlert(
        typeof message === 'string'
          ? message
          : t('CONVERSATION.CONTEXT_MENU.FORWARD_MESSAGE.CONTACT_CREATE_ERROR')
      );
      return;
    } finally {
      isCreatingContact.value = false;
    }
  }

  selectedContact.value = contact;
  contacts.value = [];
  showContactsDropdown.value = false;
  await loadTargetInboxes(contact);
};

const clearSelectedContact = () => {
  selectedContact.value = null;
  targetInbox.value = null;
  targetInboxes.value = [];
  showInboxesDropdown.value = false;
};

const handleInboxAction = ({ value }) => {
  targetInbox.value = targetInboxes.value.find(
    inbox => Number(inbox.id) === Number(value)
  );
  showInboxesDropdown.value = false;
};

const getForwardErrorMessage = code => {
  if (code === 'reply_window_closed') {
    return t(
      'CONVERSATION.CONTEXT_MENU.FORWARD_MESSAGE.ERRORS.REPLY_WINDOW_CLOSED'
    );
  }
  if (code === 'same_conversation') {
    return t(
      'CONVERSATION.CONTEXT_MENU.FORWARD_MESSAGE.ERRORS.SAME_CONVERSATION'
    );
  }
  if (code === 'target_not_reachable') {
    return t(
      'CONVERSATION.CONTEXT_MENU.FORWARD_MESSAGE.ERRORS.TARGET_NOT_REACHABLE'
    );
  }
  if (code === 'unsupported_message') {
    return t(
      'CONVERSATION.CONTEXT_MENU.FORWARD_MESSAGE.ERRORS.UNSUPPORTED_MESSAGE'
    );
  }
  return null;
};

const handleForwardError = error => {
  const errorMessage = getForwardErrorMessage(error.response?.data?.code);
  if (errorMessage) {
    useAlert(errorMessage);
    return;
  }

  const message = parseAPIErrorResponse(error);
  useAlert(
    typeof message === 'string'
      ? message
      : t('CONVERSATION.CONTEXT_MENU.FORWARD_MESSAGE.ERRORS.GENERIC')
  );
};

const forwardMessage = async () => {
  if (isConfirmDisabled.value) return;

  isForwarding.value = true;
  try {
    const { data } = await MessageAPI.forward(
      conversationId.value,
      props.message.id,
      {
        target_contact_id: selectedContact.value.id,
        target_inbox_id: targetInbox.value.id,
      }
    );
    const action = {
      type: 'link',
      to: `/app/accounts/${currentAccountId.value}/conversations/${data.conversation_id}`,
      message: t('CONVERSATION.CONTEXT_MENU.FORWARD_MESSAGE.OPEN_CONVERSATION'),
    };
    useTrack(CONVERSATION_EVENTS.FORWARDED_A_MESSAGE);
    dialogRef.value?.close();
    useAlert(t('CONVERSATION.CONTEXT_MENU.FORWARD_MESSAGE.SUCCESS'), action);
  } catch (error) {
    handleForwardError(error);
  } finally {
    isForwarding.value = false;
  }
};

const open = async () => {
  reset();
  dialogRef.value?.open();
  await store.dispatch('inboxes/get');
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    :title="t('CONVERSATION.CONTEXT_MENU.FORWARD_MESSAGE.TITLE')"
    :description="t('CONVERSATION.CONTEXT_MENU.FORWARD_MESSAGE.DESCRIPTION')"
    :confirm-button-label="
      t('CONVERSATION.CONTEXT_MENU.FORWARD_MESSAGE.CONFIRM')
    "
    :cancel-button-label="t('CONVERSATION.CONTEXT_MENU.FORWARD_MESSAGE.CANCEL')"
    :disable-confirm-button="isConfirmDisabled"
    :is-loading="isForwarding"
    @confirm="forwardMessage"
    @close="reset"
  >
    <div class="flex flex-col gap-4">
      <div class="rounded-lg bg-n-alpha-2 px-4 py-3">
        <p class="mb-1 text-xs font-medium uppercase text-n-slate-10">
          {{ t('CONVERSATION.CONTEXT_MENU.FORWARD_MESSAGE.PREVIEW') }}
        </p>
        <p
          v-if="previewContent"
          class="mb-0 line-clamp-3 whitespace-pre-wrap text-sm text-n-slate-12"
        >
          {{ previewContent }}
        </p>
        <p v-if="attachmentCount" class="mb-0 mt-1 text-xs text-n-slate-11">
          {{
            t('CONVERSATION.CONTEXT_MENU.FORWARD_MESSAGE.ATTACHMENTS', {
              count: attachmentCount,
            })
          }}
        </p>
      </div>

      <div class="rounded-lg border border-n-weak bg-n-alpha-1">
        <ContactSelector
          :contacts="contacts"
          :selected-contact="selectedContact"
          :show-contacts-dropdown="showContactsDropdown"
          :is-loading="isSearching"
          :is-creating-contact="isCreatingContact"
          :contactable-inboxes-list="contactableInboxesList"
          :show-inboxes-dropdown="showInboxesDropdown"
          @search-contacts="handleContactSearch"
          @set-selected-contact="handleSelectedContact"
          @clear-selected-contact="clearSelectedContact"
          @update-dropdown="handleDropdownUpdate"
        />
        <div class="border-t border-n-weak">
          <InboxSelector
            :target-inbox="targetInbox"
            :selected-contact="selectedContact"
            :show-inboxes-dropdown="showInboxesDropdown"
            :contactable-inboxes-list="contactableInboxesList"
            :is-fetching-inboxes="isFetchingInboxes"
            @update-inbox="targetInbox = $event"
            @toggle-dropdown="showInboxesDropdown = $event"
            @handle-inbox-action="handleInboxAction"
          />
        </div>
      </div>

      <p
        v-if="shouldShowNoTargets"
        class="mb-0 rounded-lg bg-n-amber-3 px-4 py-3 text-sm text-n-amber-11"
      >
        {{ t('CONVERSATION.CONTEXT_MENU.FORWARD_MESSAGE.NO_TARGETS') }}
      </p>
    </div>
  </Dialog>
</template>
